import React from 'react';
import { localizedValue } from '../../../localization/presentationLanguage';

const synText = (es, en) => localizedValue({ es, en });

const SIGNS = [
  'aries',
  'taurus',
  'gemini',
  'cancer',
  'leo',
  'virgo',
  'libra',
  'scorpio',
  'sagittarius',
  'capricorn',
  'aquarius',
  'pisces',
];

const SIGN_META = {
  aries: { label: () => 'Aries', emoji: '♈', element: () => synText('Fuego', 'Fire') },
  taurus: { label: () => synText('Tauro', 'Taurus'), emoji: '♉', element: () => synText('Tierra', 'Earth') },
  gemini: { label: () => synText('Géminis', 'Gemini'), emoji: '♊', element: () => synText('Aire', 'Air') },
  cancer: { label: () => synText('Cáncer', 'Cancer'), emoji: '♋', element: () => synText('Agua', 'Water') },
  leo: { label: () => 'Leo', emoji: '♌', element: () => synText('Fuego', 'Fire') },
  virgo: { label: () => 'Virgo', emoji: '♍', element: () => synText('Tierra', 'Earth') },
  libra: { label: () => 'Libra', emoji: '♎', element: () => synText('Aire', 'Air') },
  scorpio: { label: () => synText('Escorpio', 'Scorpio'), emoji: '♏', element: () => synText('Agua', 'Water') },
  sagittarius: { label: () => synText('Sagitario', 'Sagittarius'), emoji: '♐', element: () => synText('Fuego', 'Fire') },
  capricorn: { label: () => synText('Capricornio', 'Capricorn'), emoji: '♑', element: () => synText('Tierra', 'Earth') },
  aquarius: { label: () => synText('Acuario', 'Aquarius'), emoji: '♒', element: () => synText('Aire', 'Air') },
  pisces: { label: () => synText('Piscis', 'Pisces'), emoji: '♓', element: () => synText('Agua', 'Water') },
};

const styles = {
  card: {
    borderRadius: 24,
    padding: 18,
    border: '1px solid rgba(218,165,32,0.18)',
    background: 'rgba(10, 7, 20, 0.76)',
    backdropFilter: 'blur(10px)',
    boxShadow: '0 10px 40px rgba(0,0,0,0.22)',
    marginBottom: 22,
  },
  desktopGrid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0,1fr) auto minmax(0,1fr) auto',
    gap: 12,
    alignItems: 'end',
  },
  mobileGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: 12,
  },
  fieldWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    minWidth: 0,
  },
  label: {
    fontSize: 12,
    fontWeight: 800,
    textTransform: 'uppercase',
    letterSpacing: 0.35,
    color: 'rgba(255,243,196,0.74)',
  },
  select: {
    width: '100%',
    height: 52,
    borderRadius: 14,
    border: '1px solid rgba(218,165,32,0.20)',
    background: 'rgba(255,255,255,0.04)',
    color: '#FFF3C4',
    padding: '0 14px',
    fontSize: 15,
    fontWeight: 700,
    outline: 'none',
  },
  heart: {
    alignSelf: 'center',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 52,
    height: 52,
    borderRadius: 16,
    color: '#F7D774',
    fontSize: 22,
    background: 'linear-gradient(180deg, rgba(218,165,32,0.18), rgba(218,165,32,0.06))',
    border: '1px solid rgba(218,165,32,0.20)',
    boxShadow: '0 0 20px rgba(218,165,32,0.10)',
  },
  mobileHeart: {
    justifySelf: 'center',
  },
  button: {
    height: 52,
    borderRadius: 14,
    border: '1px solid rgba(218,165,32,0.25)',
    background: 'linear-gradient(135deg, #DAA520 0%, #F2C94C 100%)',
    color: '#2a1600',
    fontWeight: 900,
    fontSize: 15,
    padding: '0 20px',
    cursor: 'pointer',
    boxShadow: '0 12px 30px rgba(218,165,32,0.18)',
  },
  mobileButton: {
    width: '100%',
  },
  buttonDisabled: {
    opacity: 0.7,
    cursor: 'not-allowed',
  },
  helperRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
    marginTop: 12,
    color: 'rgba(255,243,196,0.65)',
    fontSize: 13,
  },
};

function labelForSign(sign) {
  return SIGN_META[sign]?.label?.() || sign;
}

function metaForSign(sign) {
  const meta = SIGN_META[sign];
  return meta ? { ...meta, label: meta.label(), element: meta.element() } : { label: sign, emoji: '✦', element: '—' };
}

export default function QuickCompatibilityForm({
  signA = 'leo',
  signB = 'pisces',
  onChangeSignA,
  onChangeSignB,
  onSubmit,
  loading = false,
  isMobile = false,
}) {
  const pairMeta = {
    a: metaForSign(signA),
    b: metaForSign(signB),
  };

  return (
    <div style={styles.card}>
      <div style={isMobile ? styles.mobileGrid : styles.desktopGrid}>
        <div style={styles.fieldWrap}>
          <label htmlFor="signA-select" style={styles.label}>{synText('Primer signo', 'First sign')}</label>
          <select
            id="signA-select"
            style={styles.select}
            value={signA}
            onChange={(e) => onChangeSignA?.(e.target.value)}
          >
            {SIGNS.map((sign) => (
              <option key={sign} value={sign}>
                {labelForSign(sign)}
              </option>
            ))}
          </select>
        </div>

        <div style={{ ...styles.heart, ...(isMobile ? styles.mobileHeart : null) }}>
          ❤
        </div>

        <div style={styles.fieldWrap}>
          <label htmlFor="signB-select" style={styles.label}>{synText('Segundo signo', 'Second sign')}</label>
          <select
            id="signB-select"
            style={styles.select}
            value={signB}
            onChange={(e) => onChangeSignB?.(e.target.value)}
          >
            {SIGNS.map((sign) => (
              <option key={sign} value={sign}>
                {labelForSign(sign)}
              </option>
            ))}
          </select>
        </div>

        <button
          type="button"
          onClick={onSubmit}
          disabled={loading}
          style={{
            ...styles.button,
            ...(loading ? styles.buttonDisabled : null),
            ...(isMobile ? styles.mobileButton : null),
          }}
        >
          {loading ? synText('Calculando…', 'Calculating…') : synText('Calcular compatibilidad', 'Calculate compatibility')}
        </button>
      </div>

      <div style={styles.helperRow}>
        <span>
          {pairMeta.a.emoji} {pairMeta.a.label} · {pairMeta.a.element}
        </span>
        <span>
          {pairMeta.b.emoji} {pairMeta.b.label} · {pairMeta.b.element}
        </span>
        {signA === signB ? (
          <span>
            {synText('Estás comparando el mismo signo consigo mismo.', 'You are comparing the same sign with itself.')}
          </span>
        ) : null}
      </div>
    </div>
  );
}
